import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { getValidToken } from "../../../utils/tokenManager";
import { useContactContext } from "../../../contexts/ContactContext";
import MobileTopbar from "./MobileTopbar";
import MobileContactCard from "./MobileContactCard";
import MobileOutcomeCard from "./MobileOutcomeCard";
import MobileTabBar from "./MobileTabBar";
import MobileLeadsSheet from "./MobileLeadsSheet";

/**
 * Mobile orchestrator for the agent workspace. Same route
 * (/agent/workspace/:callType), same ContactContext data, same
 * clientDetails fetch, same "clear selection -> effect picks next
 * contact" advance pattern as desktop's AgentWorkspace.jsx - only the
 * layout (single column, bottom tabs, a sheet instead of a sidebar
 * list) is mobile-specific.
 */
const AgentWorkspaceMobile = () => {
  const { callType } = useParams();
  const { t } = useTranslation();
  const {
    coldCallContacts,
    followUpContacts,
    meetingContacts,
    meetingRecovery,
  } = useContactContext();

  const [selectedContactId, setSelectedContactId] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const agentName = sessionStorage.getItem("userName");
  const companyName = sessionStorage.getItem("clientName");
  const clinentId = sessionStorage.getItem("clinentId");

  const listsByType = {
    "cold-calls": coldCallContacts,
    "follow-up": followUpContacts,
    meeting: meetingContacts,
    "meeting-recovery": meetingRecovery,
  };

  const currentList = useMemo(() => listsByType[callType] || [], [
    callType,
    coldCallContacts,
    followUpContacts,
    meetingContacts,
    meetingRecovery,
  ]);

  const selectedContact =
    currentList.find((c) => c.contactId === selectedContactId) ||
    currentList[0] ||
    null;

  // Same pattern as desktop: MobileOutcomeCard clears the selection after
  // a successful submit, which makes this effect fall through to picking
  // the next contact waiting in the queue.
  useEffect(() => {
    if (!currentList.some((c) => c.contactId === selectedContactId)) {
      setSelectedContactId(currentList[0]?.contactId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentList]);

  const fetchClientDetails = useCallback(async (signal) => {
    if (!clinentId) return;
    try {
      const accessToken = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_APIBASEURL}/api/clients/${clinentId}`,
        { headers: { Authorization: `Bearer ${accessToken}` }, signal }
      );
      if (!signal?.aborted) setClientDetails(response.data);
    } catch (err) {
      if (!axios.isCancel(err)) console.error("Error fetching client details:", err);
    }
  }, [clinentId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchClientDetails(controller.signal);
    return () => controller.abort();
  }, [fetchClientDetails]);

  const counts = {
    "cold-calls": coldCallContacts.length,
    "follow-up": followUpContacts.length,
    meeting: meetingContacts.length,
    "meeting-recovery": meetingRecovery.length,
  };

  return (
    <div className="flex flex-col h-screen bg-secondaryBg">
      <MobileTopbar agentName={agentName} companyName={companyName} />

      <div className="flex-1 overflow-y-auto">
        {selectedContact ? (
          <>
            <MobileContactCard
              contact={selectedContact}
              clientDetails={clientDetails}
              callType={callType}
            />
            <MobileOutcomeCard
              contact={selectedContact}
              callType={callType}
              onNextContact={() => setSelectedContactId(null)}
            />
          </>
        ) : (
          <div className="flex items-center justify-center py-16 px-6 text-center text-gray-400 text-sm">
            {t("agent.workspace.noContacts", "No hay contactos en esta categoria")}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="flex items-center justify-center gap-1.5 bg-white border-t border-gray-200 py-2.5 text-[13px] font-bold text-icon flex-shrink-0"
      >
        <svg viewBox="0 0 24 24" fill="none" width="15" height="15">
          <path d="M4 6h16M4 12h16M4 18h10" stroke="#0B3C88" strokeWidth="2" strokeLinecap="round" />
        </svg>
        {t("agent.mobile.allLeads", "Ver todos los leads")}
        <span className="bg-[#FFF1DB] text-[#A15C00] text-[9px] font-extrabold px-2 py-0.5 rounded-full ml-1">
          {currentList.length}
        </span>
      </button>

      <MobileTabBar activeCallType={callType} counts={counts} />

      <MobileLeadsSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        contacts={currentList}
        selectedContactId={selectedContact?.contactId}
        onSelect={(id) => setSelectedContactId(id)}
        total={currentList.length}
      />
    </div>
  );
};

export default AgentWorkspaceMobile;